/**
 * The canonical topic vocabulary.
 *
 * This one list is the vocabulary for BOTH `topic_weights.topic` (what a client
 * covers) and `stories.category` / `headlines.topic` (what a story was pulled
 * for). They must stay the same set of ids.
 *
 * Thirty three topics, split by the reach each one needs, which is the same
 * split onboarding asks about:
 *
 *   local    (19) bound to the coverage area. A story only surfaces if it
 *                 happened where the creator covers.
 *   national (14) reach past the coverage area by design. Scope is set per pill:
 *                 national, country or worldwide.
 *
 * Ids from the original twenty-topic list are kept exactly, so nothing already
 * stored against them changes meaning. Labels are Brian's short forms: the icon
 * carries the qualifier, so "Development" rather than "Development &
 * Construction", and the ampersand survives only where both halves carry weight.
 */
export type TopicScope = 'local' | 'national'

export interface Topic {
  id: string
  label: string
  scope: TopicScope
  /** File stem under /topics/, e.g. "realestate" for topic-realestate.svg. */
  icon: string
  /** One of Brian's 13 topics where a season means something (festivals,
   *  markets, harvest, music, sports, school, outdoor, weather, elections). The
   *  other twenty run flat and must never trigger seasonal advice or a clock. */
  seasonal: boolean
  /** Politics gate: when the account has politics switched off, stories under
   *  these topics are filtered before they are written, never softened after. */
  political: boolean
  /** Family-friendly gate: topics whose stories most often carry fatalities,
   *  violent crime or graphic detail. The gate still screens every topic; this
   *  flag only tells the prompt where to be strictest. */
  sensitive: boolean
  /** Lowercase match terms, used to spot a topic in a headline and to seed
   *  the prompt. */
  keywords: string[]
}

export const TOPICS: readonly Topic[] = [
  // ---------------- Local (19) ----------------
  {
    id: 'real-estate-market', label: 'Real Estate', scope: 'local', icon: 'realestate',
    seasonal: false, political: false, sensitive: false,
    keywords: ['real estate', 'housing', 'home sales', 'mortgage', 'listing', 'property', 'rent'],
  },
  {
    id: 'new-business-openings', label: 'New Business', scope: 'local', icon: 'newbusiness',
    seasonal: false, political: false, sensitive: false,
    keywords: ['opening', 'new business', 'storefront', 'expands', 'launches', 'grand opening'],
  },
  {
    id: 'civic-government', label: 'Civic & Government', scope: 'local', icon: 'civic',
    seasonal: true, political: true, sensitive: false,
    keywords: ['council', 'county', 'commission', 'mayor', 'ordinance', 'budget', 'vote'],
  },
  {
    id: 'events', label: 'Events', scope: 'local', icon: 'events',
    seasonal: true, political: false, sensitive: false,
    keywords: ['festival', 'event', 'parade', 'concert', 'fair', 'market', 'fundraiser'],
  },
  {
    id: 'food-drink', label: 'Food & Drink', scope: 'local', icon: 'food',
    seasonal: true, political: false, sensitive: false,
    keywords: ['restaurant', 'cafe', 'brewery', 'winery', 'menu', 'chef', 'bakery'],
  },
  {
    id: 'safety-alerts', label: 'Safety Alerts', scope: 'local', icon: 'safety',
    seasonal: false, political: false, sensitive: true,
    keywords: ['police', 'fire', 'crash', 'closure', 'evacuation', 'warning', 'sheriff'],
  },
  {
    id: 'schools', label: 'Schools', scope: 'local', icon: 'schools',
    seasonal: true, political: false, sensitive: false,
    keywords: ['school', 'district', 'student', 'teacher', 'graduation', 'campus'],
  },
  {
    id: 'development-construction', label: 'Development', scope: 'local', icon: 'development',
    seasonal: false, political: false, sensitive: false,
    keywords: ['development', 'construction', 'permit', 'zoning', 'subdivision', 'apartments', 'annexation'],
  },
  {
    id: 'roads-transit', label: 'Roads & Transit', scope: 'local', icon: 'roads',
    seasonal: false, political: false, sensitive: false,
    keywords: ['road', 'traffic', 'closure', 'transit', 'bus', 'highway', 'detour', 'paving'],
  },
  {
    id: 'jobs-hiring', label: 'Jobs & Hiring', scope: 'local', icon: 'jobs',
    seasonal: false, political: false, sensitive: false,
    keywords: ['jobs', 'hiring', 'employer', 'layoffs', 'workforce', 'wages'],
  },
  {
    id: 'home-garden', label: 'Home & Garden', scope: 'local', icon: 'homegarden',
    seasonal: false, political: false, sensitive: false,
    keywords: ['garden', 'nursery', 'home', 'landscaping', 'plants', 'remodel'],
  },
  {
    id: 'outdoor-recreation', label: 'Outdoor Recreation', scope: 'local', icon: 'outdoor',
    seasonal: true, political: false, sensitive: false,
    keywords: ['trail', 'park', 'hiking', 'river', 'camping', 'ski', 'lake'],
  },
  {
    id: 'tourism-destination', label: 'Tourism', scope: 'local', icon: 'tourism',
    seasonal: true, political: false, sensitive: false,
    keywords: ['tourism', 'visitors', 'hotel', 'attraction', 'wine country', 'getaway'],
  },
  {
    id: 'snow-weather', label: 'Snow & Weather', scope: 'local', icon: 'weather',
    seasonal: true, political: false, sensitive: false,
    keywords: ['weather', 'snow', 'storm', 'rain', 'heat', 'wildfire smoke', 'forecast'],
  },
  {
    id: 'local-sports', label: 'Local Sports', scope: 'local', icon: 'sports',
    seasonal: true, political: false, sensitive: false,
    keywords: ['team', 'game', 'season', 'coach', 'playoffs', 'high school sports'],
  },
  {
    id: 'arts-culture', label: 'Arts & Culture', scope: 'local', icon: 'arts',
    seasonal: true, political: false, sensitive: false,
    keywords: ['art', 'gallery', 'theatre', 'museum', 'exhibit', 'mural', 'performance'],
  },
  {
    id: 'agriculture-farming', label: 'Agriculture', scope: 'local', icon: 'agriculture',
    seasonal: true, political: false, sensitive: false,
    keywords: ['farm', 'harvest', 'crop', 'agriculture', 'ranch', 'vineyard', 'growers'],
  },
  {
    id: 'nonprofits-causes', label: 'Nonprofits', scope: 'local', icon: 'nonprofits',
    seasonal: false, political: false, sensitive: false,
    keywords: ['nonprofit', 'fundraiser', 'volunteer', 'donation', 'charity', 'food bank'],
  },
  {
    id: 'health-wellness', label: 'Health', scope: 'local', icon: 'health',
    seasonal: false, political: false, sensitive: false,
    keywords: ['hospital', 'clinic', 'health', 'medical', 'wellness', 'public health'],
  },

  // ---------------- National (14) ----------------
  {
    id: 'politics', label: 'Politics', scope: 'national', icon: 'politics',
    seasonal: true, political: true, sensitive: false,
    keywords: ['election', 'congress', 'senate', 'campaign', 'governor', 'president', 'ballot'],
  },
  {
    id: 'geopolitics', label: 'Geopolitics', scope: 'national', icon: 'geopolitics',
    seasonal: false, political: true, sensitive: false,
    keywords: ['diplomacy', 'summit', 'sanctions', 'treaty', 'foreign policy', 'united nations'],
  },
  {
    id: 'military', label: 'Military', scope: 'national', icon: 'military',
    seasonal: false, political: true, sensitive: true,
    keywords: ['military', 'army', 'navy', 'air force', 'veterans', 'pentagon', 'deployment'],
  },
  {
    id: 'finance', label: 'Finance', scope: 'national', icon: 'finance',
    seasonal: false, political: false, sensitive: false,
    keywords: ['markets', 'stocks', 'interest rates', 'inflation', 'fed', 'earnings', 'economy'],
  },
  {
    id: 'technology', label: 'Technology', scope: 'national', icon: 'technology',
    seasonal: false, political: false, sensitive: false,
    keywords: ['technology', 'ai', 'software', 'startup', 'apple', 'google', 'launch'],
  },
  {
    id: 'space', label: 'Space', scope: 'national', icon: 'space',
    seasonal: false, political: false, sensitive: false,
    keywords: ['nasa', 'spacex', 'launch', 'rocket', 'astronaut', 'moon', 'mars'],
  },
  {
    id: 'celebrity-news', label: 'Celebrity News', scope: 'national', icon: 'celebritynews',
    seasonal: false, political: false, sensitive: false,
    keywords: ['celebrity', 'actor', 'actress', 'star', 'red carpet', 'hollywood'],
  },
  {
    id: 'reality-tv', label: 'Reality TV', scope: 'national', icon: 'realitytv',
    seasonal: false, political: false, sensitive: false,
    keywords: ['reality tv', 'bachelor', 'survivor', 'housewives', 'season finale', 'cast'],
  },
  {
    id: 'true-crime', label: 'True Crime', scope: 'national', icon: 'truecrime',
    seasonal: false, political: false, sensitive: true,
    keywords: ['trial', 'verdict', 'case', 'investigation', 'cold case', 'documentary'],
  },
  {
    id: 'cars', label: 'Cars', scope: 'national', icon: 'cars',
    seasonal: false, political: false, sensitive: false,
    keywords: ['car', 'ev', 'automaker', 'recall', 'truck', 'tesla', 'ford'],
  },
  {
    id: 'history-heritage', label: 'History', scope: 'national', icon: 'history',
    seasonal: false, political: false, sensitive: false,
    keywords: ['history', 'historic', 'heritage', 'anniversary', 'landmark', 'archive'],
  },
  {
    id: 'artists-bands', label: 'Artists & Bands', scope: 'national', icon: 'artistsbands',
    seasonal: true, political: false, sensitive: false,
    keywords: ['tour', 'album', 'band', 'singer', 'concert', 'single', 'grammy'],
  },
  {
    id: 'pro-sports', label: 'Pro Sports', scope: 'national', icon: 'prosports',
    seasonal: true, political: false, sensitive: false,
    keywords: ['nfl', 'nba', 'mlb', 'nhl', 'mls', 'trade', 'championship'],
  },
  {
    // Brian's own dumbbell (settings-topics v1.4, GoGrov-Demo-v2).
    id: 'fitness', label: 'Fitness', scope: 'national', icon: 'fitness',
    seasonal: false, political: false, sensitive: false,
    keywords: ['fitness', 'workout', 'running', 'marathon', 'gym', 'training'],
  },
] as const

export const TOPIC_IDS: readonly string[] = TOPICS.map((t) => t.id)

export const LOCAL_TOPICS = TOPICS.filter((t) => t.scope === 'local')
export const NATIONAL_TOPICS = TOPICS.filter((t) => t.scope === 'national')

export function isTopicId(value: string): boolean {
  return TOPIC_IDS.includes(value)
}

export function getTopic(id: string | null | undefined): Topic | undefined {
  return id ? TOPICS.find((t) => t.id === id) : undefined
}

export function topicLabel(id: string | null | undefined): string {
  return getTopic(id)?.label ?? (id ?? '')
}

export function topicScope(id: string | null | undefined): TopicScope {
  return getTopic(id)?.scope ?? 'local'
}

// ---------------- Short labels (Brian's feed-controls.js LABEL) ----------------

/**
 * Brian's short names, for one-line summaries ("Real estate 30%, Events 20%"),
 * Fewer like this and the "Tuned for" line. Topic cards and tiles keep `label`.
 */
export const TOPIC_SHORT: Readonly<Record<string, string>> = {
  'real-estate-market': 'Real estate', 'new-business-openings': 'New business', 'civic-government': 'Civic',
  'events': 'Events', 'food-drink': 'Food & drink', 'safety-alerts': 'Safety', 'schools': 'Schools',
  'development-construction': 'Development', 'roads-transit': 'Roads', 'jobs-hiring': 'Jobs',
  'home-garden': 'Home & garden', 'outdoor-recreation': 'Outdoor', 'tourism-destination': 'Tourism',
  'snow-weather': 'Weather', 'local-sports': 'Local sports', 'arts-culture': 'Arts', 'agriculture-farming': 'Agriculture',
  'nonprofits-causes': 'Nonprofits', 'health-wellness': 'Health', 'politics': 'Politics', 'geopolitics': 'World news',
  'military': 'Military', 'finance': 'Finance', 'technology': 'Technology', 'space': 'Space',
  'celebrity-news': 'Celebrity news', 'reality-tv': 'Reality TV', 'true-crime': 'True crime', 'cars': 'Cars',
  'history-heritage': 'History', 'artists-bands': 'Music', 'pro-sports': 'Pro sports', 'fitness': 'Fitness',
  'aviation': 'Aviation',
}

export function shortLabel(id: string | null | undefined): string {
  if (!id) return ''
  return TOPIC_SHORT[id] ?? getTopic(id)?.label ?? WATCH_ONLY_TOPICS.find((t) => t.id === id)?.label ?? id
}

/**
 * Types that exist only as a breaking-news watch, never in the topic mix
 * (Brian: aviation has a photo, an icon and a watch type, but no topic).
 */
export const WATCH_ONLY_TOPICS: readonly { id: string; label: string; icon: string }[] = [
  { id: 'aviation', label: 'Aviation', icon: 'aviation' },
] as const

// ---------------- Season words (Brian's settings-topics.html SEASONS) ----------------

/**
 * Season words are known without research: any keyword containing one gets its
 * season and its clock the moment it is typed, on a seasonal topic only. Years
 * are ignored ("bend christmas 2028"); ambiguous words only match as phrases,
 * and `no` lists phrases that must not match ("fair housing").
 *
 * open / close: 'MM-DD'; or 'nth:<n>:<weekday 0-6>:<MM>[±days]' (n = -1 for the
 * last); or 'easter[±days]'. ramp: days of build-up before it opens (it reads
 * Warming); lead: days before close it reads Ends.
 */
export interface SeasonWord {
  key: string
  match: string[]
  no?: string[]
  open: string
  close: string
  ramp: number
  lead: number
  shape: 'cliff' | 'fade'
  next: string[]
}

export const SEASON_WORDS: readonly SeasonWord[] = [
  { key: 'christmas', match: ['christmas', 'xmas', 'holiday lights', 'tree lighting', 'santa'], open: '11-24', close: '12-26', ramp: 30, lead: 7, shape: 'cliff', next: ['new year’s eve', 'winter events'] },
  { key: 'holiday market', match: ['holiday market', 'holiday markets', 'christmas market', 'winter market'], open: '11-20', close: '12-24', ramp: 21, lead: 7, shape: 'cliff', next: ['winter events', 'new year’s eve'] },
  { key: 'thanksgiving', match: ['thanksgiving', 'turkey trot'], open: 'nth:4:4:11-7', close: 'nth:4:4:11+1', ramp: 21, lead: 3, shape: 'cliff', next: ['black friday', 'christmas'] },
  { key: 'black friday', match: ['black friday', 'small business saturday', 'cyber monday'], open: 'nth:4:4:11+1', close: 'nth:4:4:11+4', ramp: 14, lead: 2, shape: 'cliff', next: ['holiday market', 'christmas'] },
  { key: 'halloween', match: ['halloween', 'trick or treat', 'haunted house', 'costume contest'], open: '10-01', close: '10-31', ramp: 21, lead: 7, shape: 'cliff', next: ['thanksgiving', 'fall festivals'] },
  { key: 'pumpkin patch', match: ['pumpkin patch', 'corn maze', 'pumpkin'], open: '09-25', close: '10-31', ramp: 21, lead: 10, shape: 'cliff', next: ['holiday market', 'tree lots'] },
  { key: 'oktoberfest', match: ['oktoberfest'], open: '09-15', close: '09-30', ramp: 62, lead: 14, shape: 'cliff', next: ['fall festivals', 'harvest events'] },
  { key: 'easter', match: ['easter', 'egg hunt'], open: 'easter-14', close: 'easter+1', ramp: 21, lead: 5, shape: 'cliff', next: ['spring events', 'mother’s day'] },
  { key: 'mother’s day', match: ['mother’s day', "mother's day", 'mothers day'], open: 'nth:2:0:05-10', close: 'nth:2:0:05', ramp: 14, lead: 4, shape: 'cliff', next: ['memorial day', 'graduation'] },
  { key: 'fourth of july', match: ['fourth of july', 'july 4th', '4th of july', 'independence day', 'fireworks show'], open: '06-25', close: '07-05', ramp: 21, lead: 5, shape: 'cliff', next: ['county fair', 'summer concerts'] },
  { key: 'valentine', match: ['valentine', 'valentines', 'valentine’s day', "valentine's day"], open: '02-01', close: '02-14', ramp: 14, lead: 5, shape: 'cliff', next: ['spring break', 'st patrick’s day'] },
  { key: 'new year’s eve', match: ['new year’s eve', "new year's eve", 'new years eve', 'nye'], open: '12-26', close: '01-01', ramp: 10, lead: 3, shape: 'cliff', next: ['winter events'] },
  { key: 'county fair', match: ['county fair', 'state fair', 'fairgrounds', 'rodeo'], no: ['fair housing', 'fair price', 'fair market'], open: '07-15', close: '08-20', ramp: 30, lead: 10, shape: 'fade', next: ['back to school', 'harvest events'] },
  { key: 'back to school', match: ['back to school', 'first day of school', 'school supplies'], open: '08-10', close: '09-10', ramp: 21, lead: 7, shape: 'fade', next: ['homecoming', 'fall sports'] },
  { key: 'graduation', match: ['graduation', 'commencement', 'prom'], open: '05-15', close: '06-20', ramp: 21, lead: 7, shape: 'fade', next: ['summer camps', 'fourth of july'] },
  { key: 'ski season', match: ['ski season', 'opening day on the mountain', 'snowboard', 'chairlift'], open: '11-20', close: '04-15', ramp: 30, lead: 21, shape: 'fade', next: ['spring skiing', 'trail openings'] },
  { key: 'super bowl', match: ['super bowl'], open: 'nth:2:0:02-10', close: 'nth:2:0:02', ramp: 14, lead: 3, shape: 'cliff', next: ['march madness'] },
  { key: 'pride month', match: ['pride month', 'pride parade', 'pride festival'], open: '06-01', close: '06-30', ramp: 14, lead: 7, shape: 'cliff', next: ['fourth of july'] },
  { key: 'election day', match: ['election day', 'ballot measure', 'voter guide'], open: '10-01', close: 'nth:1:1:11+1', ramp: 30, lead: 7, shape: 'cliff', next: ['city council', 'legislative session'] },
]

/** One key per keyword however it is typed: lowercase, curly apostrophes, no years. */
export function normTerm(t: string): string {
  return t.toLowerCase().replace(/[’']/g, '’').replace(/\b(19|20)\d{2}\b/g, ' ')
    .replace(/[^a-z0-9’ ]+/g, ' ').replace(/\s+/g, ' ').trim()
}

/** The season a keyword carries by its words alone, or null. */
export function seasonWord(term: string): SeasonWord | null {
  const t = ` ${normTerm(term)} `
  return SEASON_WORDS.find((sw) => !(sw.no ?? []).some((n) => t.includes(` ${normTerm(n)} `))
    && sw.match.some((m) => t.includes(` ${normTerm(m)} `))) ?? null
}

const DAY_MS = 864e5
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * DAY_MS)
const mmdd = (y: number, s: string) => new Date(`${y}-${s}T12:00:00`)

function easterOf(y: number) {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451)
  const mo = Math.floor((h + l - 7 * m + 114) / 31), da = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(y, mo - 1, da, 12)
}

/** A season date in a given year, from 'MM-DD', 'nth:…' or 'easter…'. */
export function seasonDate(rule: string, y: number): Date {
  let m = rule.match(/^(\d\d)-(\d\d)$/)
  if (m) return mmdd(y, rule)
  m = rule.match(/^easter([+-]\d+)?$/)
  if (m) return addDays(easterOf(y), +(m[1] || 0))
  m = rule.match(/^nth:(-?\d):(\d):(\d\d)([+-]\d+)?$/)
  if (m) {
    const n = +m[1], dow = +m[2], mon = +m[3] - 1
    let d: Date
    if (n > 0) { d = new Date(y, mon, 1, 12); while (d.getDay() !== dow) d = addDays(d, 1); d = addDays(d, 7 * (n - 1)) }
    else { d = new Date(y, mon + 1, 0, 12); while (d.getDay() !== dow) d = addDays(d, -1) }
    return addDays(d, +(m[4] || 0))
  }
  return mmdd(y, '01-01')
}

/** The season's window that is current or next from `today` (handles seasons
 *  that cross the new year), with the day coverage starts building (wake). */
export function seasonWindow(sea: { open: string; close: string; ramp: number }, today: Date) {
  const y = today.getFullYear()
  let o = seasonDate(sea.open, y), c = seasonDate(sea.close, y)
  if (c < o) { if (today <= c) o = seasonDate(sea.open, y - 1); else c = seasonDate(sea.close, y + 1) }
  if (today > c) { o = seasonDate(sea.open, y + 1); c = seasonDate(sea.close, y + 1); if (c < o) c = seasonDate(sea.close, y + 2) }
  return { open: o, close: c, wake: addDays(o, -sea.ramp) }
}

// ---------------- Voice positions ----------------

/**
 * Newcomer, Reporter, Insider: ordered by how much authority the creator is
 * claiming, never by heat. Do not relabel these as an intensity scale.
 */
export type VoicePosition = 'newcomer' | 'reporter' | 'insider'

export const VOICE_POSITIONS: readonly {
  id: VoicePosition
  label: string
  /** What the creator claims, shown beneath the name on the script card. */
  claim: string
  /** The shape of the closing, which is where the three differ most. */
  closing: string
  line: string
}[] = [
  {
    id: 'newcomer', label: 'Newcomer', claim: 'none', closing: 'Open question',
    line: 'Curious and learning the place. Asks openly.',
  },
  {
    id: 'reporter', label: 'Reporter', claim: "the record's", closing: 'What happens next',
    line: 'No first person. The credibility is in the sourcing.',
  },
  {
    id: 'insider', label: 'Insider', claim: 'personal standing', closing: 'Direction, no verdict',
    line: 'Speaks from having been here. Gives direction.',
  },
] as const

export const DEFAULT_VOICE: VoicePosition = 'newcomer'

export function voiceLabel(v: string | null | undefined): string {
  return VOICE_POSITIONS.find((p) => p.id === v)?.label ?? 'Newcomer'
}

// ---------------- Topic mix ----------------

/**
 * Splits a 100% budget evenly across topics, handing the rounding residue to
 * the first few so the total is exactly 100.
 */
export function evenShares(ids: readonly string[]): Record<string, number> {
  const out: Record<string, number> = {}
  if (ids.length === 0) return out
  const base = Math.floor(100 / ids.length)
  let residue = 100 - base * ids.length
  for (const id of ids) {
    out[id] = base + (residue > 0 ? 1 : 0)
    if (residue > 0) residue--
  }
  return out
}

/**
 * Moves one topic's share and redistributes the difference across the other
 * UNLOCKED topics in proportion to their current values, so the mix always sums
 * to 100. A topic at 40 absorbs four times what one at 10 does.
 *
 * Locked topics are excluded from redistribution and their total is subtracted
 * from the room the dragged topic has. If every other topic is locked the move
 * is rejected and the original mix is returned unchanged: no silent partial
 * move. Rounding residue goes to the last unlocked topic so nothing drifts.
 */
export function redistribute(
  shares: Record<string, number>,
  locked: ReadonlySet<string>,
  topic: string,
  target: number,
): Record<string, number> {
  const others = Object.keys(shares).filter((t) => t !== topic && !locked.has(t))
  if (others.length === 0) return { ...shares }

  const lockedTotal = Object.keys(shares)
    .filter((t) => t !== topic && locked.has(t))
    .reduce((n, t) => n + shares[t], 0)
  const room = 100 - lockedTotal
  const value = Math.max(0, Math.min(room, Math.round(target)))
  const remainder = room - value

  const othersTotal = others.reduce((n, t) => n + shares[t], 0)
  const next: Record<string, number> = { ...shares, [topic]: value }

  // Largest remainder rather than dumping the rounding residue on one topic:
  // piling three points onto the last row made two topics that started equal
  // come out unequal, which reads as the slider having a mind of its own.
  const exact = others.map((t) => ({
    t,
    v: othersTotal === 0 ? remainder / others.length : (shares[t] / othersTotal) * remainder,
  }))
  let assigned = 0
  for (const e of exact) {
    next[e.t] = Math.floor(e.v)
    assigned += next[e.t]
  }
  exact
    .sort((a, b) => (b.v - Math.floor(b.v)) - (a.v - Math.floor(a.v)))
    .slice(0, remainder - assigned)
    .forEach((e) => { next[e.t] += 1 })
  return next
}

/**
 * Splits `count` headlines across topics by share, largest remainder first, so
 * a 50 / 30 / 20 mix over ten headlines is exactly 5 / 3 / 2. Topics at 0 get
 * nothing; that is what 0 means.
 */
export function allocateByShare(
  shares: Record<string, number>,
  count: number,
): Record<string, number> {
  const live = Object.entries(shares).filter(([, s]) => s > 0)
  const total = live.reduce((n, [, s]) => n + s, 0)
  const out: Record<string, number> = {}
  if (total === 0 || count <= 0) return out
  const raw = live.map(([t, s]) => ({ t, exact: (s / total) * count }))
  let used = 0
  for (const r of raw) {
    out[r.t] = Math.floor(r.exact)
    used += out[r.t]
  }
  raw
    .sort((a, b) => (b.exact - Math.floor(b.exact)) - (a.exact - Math.floor(a.exact)))
    .slice(0, count - used)
    .forEach((r) => { out[r.t] += 1 })
  return out
}

// ---------------- Reach: the five-rung ladder ----------------

/**
 * Brian's ladder: City · County · State · National · Global. Not every topic
 * unlocks every rung; politics is the only one available at all five.
 *
 * The curve is the topic's contribution, the place supplies the magnitude:
 *   climbing   (politics, finance, military, geopolitics) thin at city, rich higher up
 *   flattening (development, new business, safety, roads, food) records-driven,
 *              meaningless aggregated upward, so they stop at county
 *   even       (history, jobs, health, sports) reach up to national
 *
 * Subject-scoped topics have no geography at all: they are steered with
 * keywords, never a place.
 */
export type Rung = 'city' | 'county' | 'state' | 'national' | 'global'
export const RUNGS: readonly Rung[] = ['city', 'county', 'state', 'national', 'global']
export const RUNG_LABELS: Record<Rung, string> = {
  city: 'City', county: 'County', state: 'State', national: 'National', global: 'Global',
}

export const SUBJECT_SCOPED: ReadonlySet<string> = new Set([
  'artists-bands', 'cars', 'celebrity-news', 'reality-tv', 'space', 'technology', 'true-crime',
])

const LOCAL3: Rung[] = ['city', 'county', 'state']
const FLAT: Rung[] = ['city', 'county']
const EVEN: Rung[] = ['city', 'county', 'state', 'national']
const RUNG_TABLE: Record<string, Rung[]> = {
  'politics': ['city', 'county', 'state', 'national', 'global'],
  'civic-government': LOCAL3,
  'real-estate-market': LOCAL3,
  'new-business-openings': FLAT,
  'events': LOCAL3,
  'food-drink': FLAT,
  'safety-alerts': FLAT,
  'schools': LOCAL3,
  'development-construction': FLAT,
  'roads-transit': FLAT,
  'jobs-hiring': EVEN,
  'home-garden': LOCAL3,
  'outdoor-recreation': LOCAL3,
  'tourism-destination': LOCAL3,
  'snow-weather': LOCAL3,
  'local-sports': EVEN,
  'arts-culture': LOCAL3,
  'agriculture-farming': ['county', 'state', 'national'],
  'nonprofits-causes': LOCAL3,
  'health-wellness': EVEN,
  'geopolitics': ['national', 'global'],
  'military': ['state', 'national', 'global'],
  'finance': ['state', 'national', 'global'],
  'history-heritage': EVEN,
  'pro-sports': ['state', 'national', 'global'],
  'fitness': ['national', 'global'],
}

/** The rungs a topic can use. Empty for subject-scoped topics. */
export function topicRungs(id: string | null | undefined): Rung[] {
  if (!id || SUBJECT_SCOPED.has(id)) return []
  return RUNG_TABLE[id] ?? []
}

/** Where a topic starts: its narrowest rung, or national for topics that
 *  begin there. Null for subject-scoped topics. */
export function defaultReach(id: string | null | undefined): Rung | null {
  const r = topicRungs(id)
  if (!r.length) return null
  if (getTopic(id)?.scope === 'national') return r.includes('national') ? 'national' : r[0]
  return r[0]
}

/** City, county and state are searched as local news for your places; national
 *  and global are searched past them. */
export function reachLayer(reach: Rung | null | undefined, id: string): 'local' | 'national' {
  if (reach === 'city' || reach === 'county' || reach === 'state') return 'local'
  if (reach === 'national' || reach === 'global') return 'national'
  return getTopic(id)?.scope === 'local' ? 'local' : 'national'
}
